import { Controller } from '@hotwired/stimulus';
import { queueDisplaySave } from 'helpers/display_saves';

// "Show all comments for this goal" on a goal's Comments panel inside a document (a progress
// report, a Plan of Care or evaluation being written, a visit note, a discharge). Unchecked, the
// panel shows only the comments written from the same kind of document as the one on the page
// (a report shows the comments other progress reports made); checked, the goal's whole thread.
// The server draws the rows the same way on load (patients/goals_v3/_comments_filter), so nothing
// moves when this connects; this only follows the box.
//
// On a page that keeps how it was viewed (the element carries a url), the choice is saved with
// the page's other display choices, one at a time with them (helpers/display_saves), so the
// print shows the comments the page showed; a save that does not go through is announced
// (goal-comments-filter:save-failed) for the page's notice.
//
// Values:
//   type   the page's document type ("ProgressReport"), matched against each row's data-source-type
//   url    where the choice saves (absent when the page does not keep its view)
//   goal   the goal the choice is for
// Targets:
//   box      the checkbox
//   comment  one comment's row, carrying data-source-type (blank for a comment written on the goal)
//   empty    the line shown when the filter leaves nothing to show
export default class extends Controller {
  static values = { type: String, url: String, goal: String };
  static targets = ['box', 'comment', 'empty'];

  apply() {
    const all = this.hasBoxTarget && this.boxTarget.checked;
    let shown = 0;
    this.commentTargets.forEach((row) => {
      const matches = all || row.dataset.sourceType === this.typeValue;
      row.hidden = !matches;
      if (matches) shown += 1;
    });
    if (this.hasEmptyTarget) this.emptyTarget.hidden = shown > 0;
    this.save(all);
  }

  save(all) {
    if (!this.hasUrlValue || !this.urlValue || !this.hasGoalValue) return;
    const url = this.urlValue;
    const body = JSON.stringify({ comments: { [this.goalValue]: { all } } });
    queueDisplaySave(() => {
      const token = document.querySelector('meta[name="csrf-token"]');
      return fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': token ? token.getAttribute('content') : '',
        },
        credentials: 'same-origin',
        body,
      });
    })
      .then((response) => {
        // A redirect is the sign-in page: the session ended and nothing was saved.
        if (!response.ok || response.redirected) throw new Error(`Comments choice not saved (${response.status})`);
      })
      .catch(() => this.dispatch('save-failed'));
  }
}
